/**
 * DocumentSearchPanel — the Brief 91 search input grid.
 *
 * Owns the draft filter state and emits the full filter object to the parent
 * via onSearch() on submit (Enter in the phrase field or the Search button).
 * Phrase is mandatory; everything else is optional.
 */
import { useState, useCallback } from "react";
import { Search, X } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import SmartClientMatterSearch from "../SmartClientMatterSearch";
import { STAFF } from "../../config/staffConfig";

const TYPE_CHIPS = [
  { value: "email", label: "Email (.eml/.msg)" },
  { value: "word", label: "Word" },
  { value: "pdf", label: "PDF" },
  { value: "excel", label: "Excel" },
  { value: "other", label: "Other" },
];

const UPLOADERS = STAFF
  .filter((s) => s.is_active)
  .map((s) => s.full_name)
  .sort((a, b) => a.localeCompare(b));

export default function DocumentSearchPanel({ onSearch, onClear, busy }) {
  const [phrase, setPhrase] = useState("");
  const [matterRef, setMatterRef] = useState("");
  const [modifiedFrom, setModifiedFrom] = useState("");
  const [modifiedTo, setModifiedTo] = useState("");
  const [types, setTypes] = useState([]);
  const [uploader, setUploader] = useState("");
  const [clientName, setClientName] = useState("");
  const [filename, setFilename] = useState("");
  const [folder, setFolder] = useState("");
  const [sender, setSender] = useState("");
  const [recipient, setRecipient] = useState("");
  const [showMore, setShowMore] = useState(false);

  const toggleType = useCallback((value) => {
    setTypes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );
  }, []);

  const submit = useCallback(() => {
    if (!phrase.trim()) return;
    onSearch({
      phrase: phrase.trim(),
      matter_ref: matterRef || null,
      modified_from: modifiedFrom || null,
      modified_to: modifiedTo || null,
      types,
      uploader: uploader || null,
      client_name: clientName.trim() || null,
      filename: filename.trim() || null,
      folder: folder.trim() || null,
      sender: sender.trim() || null,
      recipient: recipient.trim() || null,
    });
  }, [phrase, matterRef, modifiedFrom, modifiedTo, types, uploader,
      clientName, filename, folder, sender, recipient, onSearch]);

  const clear = useCallback(() => {
    setPhrase("");
    setMatterRef("");
    setModifiedFrom("");
    setModifiedTo("");
    setTypes([]);
    setUploader("");
    setClientName("");
    setFilename("");
    setFolder("");
    setSender("");
    setRecipient("");
    if (onClear) onClear();
  }, [onClear]);

  return (
    <div className="rounded-lg border border-navy-border bg-navy-card p-4 space-y-4">
      {/* Phrase */}
      <div>
        <Label className="text-sm font-medium text-teal">Search phrase</Label>
        <div className="relative mt-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
          <Input
            data-testid="doc-search-phrase"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder='e.g. "court order"'
            className="pl-8 bg-navy-hover border-navy-border text-white placeholder:text-txt-muted focus:border-teal focus:ring-teal"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Matter filter */}
        <SmartClientMatterSearch
          label="Matter (optional)"
          placeholder="Limit to a matter…"
          mattersOnly
          showMatterRef
          onSelect={(r) => setMatterRef(r?.matter_reference || "")}
        />

        {/* Uploader / fee earner */}
        <div>
          <Label className="text-sm font-medium text-teal">Fee earner / uploader (optional)</Label>
          <select
            data-testid="doc-search-uploader"
            value={uploader}
            onChange={(e) => setUploader(e.target.value)}
            className="mt-1 w-full rounded-md bg-navy-hover border border-navy-border text-white text-sm px-2 py-2 focus:border-teal focus:ring-teal"
          >
            <option value="">Anyone</option>
            {UPLOADERS.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

        {/* Date range */}
        <div>
          <Label className="text-sm font-medium text-teal">Modified from</Label>
          <Input
            type="date"
            data-testid="doc-search-from"
            value={modifiedFrom}
            onChange={(e) => setModifiedFrom(e.target.value)}
            className="mt-1 bg-navy-hover border-navy-border text-white focus:border-teal focus:ring-teal"
          />
        </div>
        <div>
          <Label className="text-sm font-medium text-teal">Modified to</Label>
          <Input
            type="date"
            data-testid="doc-search-to"
            value={modifiedTo}
            onChange={(e) => setModifiedTo(e.target.value)}
            className="mt-1 bg-navy-hover border-navy-border text-white focus:border-teal focus:ring-teal"
          />
        </div>
      </div>

      {/* Document type chips */}
      <div>
        <Label className="text-sm font-medium text-teal">Document type</Label>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {TYPE_CHIPS.map((chip) => {
            const active = types.includes(chip.value);
            return (
              <button
                type="button"
                key={chip.value}
                data-testid={`doc-type-chip-${chip.value}`}
                aria-pressed={active}
                onClick={() => toggleType(chip.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? "bg-teal/20 text-teal border-teal"
                    : "bg-slate-800 text-slate-300 border-navy-border hover:bg-slate-700"
                }`}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* More filters (client / filename / folder / email parties) */}
      <div>
        <button
          type="button"
          data-testid="doc-more-filters-toggle"
          onClick={() => setShowMore((v) => !v)}
          className="text-xs text-teal hover:underline"
        >
          {showMore ? "− Fewer filters" : "+ More filters"}
        </button>
        {showMore && (
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-teal">Client name</Label>
              <Input
                data-testid="doc-search-client"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="e.g. Smith"
                className="mt-1 bg-navy-hover border-navy-border text-white placeholder:text-txt-muted focus:border-teal focus:ring-teal"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-teal">Filename</Label>
              <Input
                data-testid="doc-search-filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="e.g. contract"
                className="mt-1 bg-navy-hover border-navy-border text-white placeholder:text-txt-muted focus:border-teal focus:ring-teal"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-teal">Folder</Label>
              <Input
                data-testid="doc-search-folder"
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
                placeholder="e.g. Court documents"
                className="mt-1 bg-navy-hover border-navy-border text-white placeholder:text-txt-muted focus:border-teal focus:ring-teal"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-sm font-medium text-teal">Sender</Label>
                <Input
                  data-testid="doc-search-sender"
                  value={sender}
                  onChange={(e) => setSender(e.target.value)}
                  placeholder="email only"
                  className="mt-1 bg-navy-hover border-navy-border text-white placeholder:text-txt-muted focus:border-teal focus:ring-teal"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-teal">Recipient</Label>
                <Input
                  data-testid="doc-search-recipient"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="email only"
                  className="mt-1 bg-navy-hover border-navy-border text-white placeholder:text-txt-muted focus:border-teal focus:ring-teal"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          type="button"
          data-testid="doc-search-submit"
          onClick={submit}
          disabled={!phrase.trim() || busy}
          className="bg-teal hover:bg-teal/90 text-navy font-semibold"
        >
          <Search size={15} className="mr-1.5" /> Search
        </Button>
        <Button
          type="button"
          data-testid="doc-search-clear"
          variant="outline"
          onClick={clear}
          className="border-navy-border text-slate-300 hover:bg-slate-700"
        >
          <X size={15} className="mr-1.5" /> Clear
        </Button>
      </div>
    </div>
  );
}
